import React from "react";
import { Box } from "@mui/material";

/**
 * Normalize status to a predictable key
 * eg: "so_created", "SO CREATED", "So-Created" → "SO_CREATED"
 */
const normalizeStatusKey = (status: string) =>
  status
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();

/**
 * Convert status key to readable label
 * eg: "SO_CREATED" → "SO Created"
 */
const toReadableLabel = (statusKey: string) =>
  statusKey
    .toLowerCase()
    .split("_")
    .map((word) =>
      word.length <= 2
        ? word.toUpperCase() // SO, PI, PO
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");

/**
 * Centralized status config
 * (keys must be NORMALIZED)
 */
export const STATUS_CONFIG: Record<
  string,
  {
    label?: string;
    color: string;
    textColor?: string;
    borderColor?: string;
  }
> = {
  /**
   * ============================================================
   * INVENTORY STATUSES
   * ============================================================
   */

  DAMAGED: {
    color: "#FDE8E8",
    textColor: "#7A1F1F",
    borderColor: "#E11D48",
  },

  ROBUST: {
    color: "#E6FFFA",
    textColor: "#065F46",
    borderColor: "#14B8A6",
  },

  GOOD: {
    color: "#E6FFFA",
    textColor: "#065F46",
    borderColor: "#14B8A6",
  },

  LOW_STOCK: {
    color: "#FEF3C7", // soft amber background
    textColor: "#92400E", // deep amber text
    borderColor: "#F59E0B",
  },

  IN_STOCK: {
    color: "#EFF6FF", // calm light blue
    textColor: "#1E40AF", // strong blue text
    borderColor: "#60A5FA",
  },

  /**
   * ============================================================
   * PURCHASE MANAGEMENT & INWARD STATUSES
   * ============================================================
   */

  PO_CREATED: {
    color: "#F3E8FF",
    textColor: "#5B21B6",
    borderColor: "#C084FC",
  },

  APPROVED: {
    color: "#ECFEFF",
    textColor: "#155E75",
    borderColor: "#06B6D4",
  },

  PARTIAL_PO_CREATED: {
    color: "#E0E7FF",
    textColor: "#3730A3",
    borderColor: "#818CF8",
  },

  REJECTED: {
    color: "#FEF2F2",
    textColor: "#991B1B",
    borderColor: "#DC2626",
  },

  PARTIALLY_INWARDED: {
    color: "#E0F2FE",
    textColor: "#075985",
    borderColor: "#0284C7",
  },

  COMPLETE_INWARDED: {
    color: "#ECFDF3",
    textColor: "#166534",
    borderColor: "#22C55E",
  },

  /**
   * ============================================================
   * SALES ORDER STATUSES
   * ============================================================
   */

  SO_CREATED: {
    color: "#EFF6FF",
    textColor: "#1E40AF",
    borderColor: "#3B82F6",
  },

  CREATED: {
    color: "#FAF5FF",
    textColor: "#6D28D9",
    borderColor: "#8B5CF6",
  },

  PI_GENERATED: {
    color: "#FFF7ED",
    textColor: "#9A3412",
    borderColor: "#FB923C",
  },

  ISSUED_FOR_PACKING: {
    color: "#F0FDFA",
    textColor: "#0F766E",
    borderColor: "#2DD4BF",
  },

  PACKING_INITIATED: {
    color: "#FDF4FF",
    textColor: "#86198F",
    borderColor: "#D946EF",
  },

  PACKING_INITAITED: {
    color: "#FCE7F3",
    textColor: "#9D174D",
    borderColor: "#EC4899",
  },

  PENDING_ISSUE: {
    color: "#FFFBEB",
    textColor: "#92400E",
    borderColor: "#F59E0B",
  },

  PARTIALLY_DISPATCHED: {
    color: "#E0F2FE", // light sky background
    textColor: "#075985", // deep sky blue text
    borderColor: "#38BDF8", // sky blue border
  },

  /**
   * ============================================================
   * SERVICE ORDER STATUSES
   * ============================================================
   */

  SRV_CREATED: {
    color: "#F1F5F9",
    textColor: "#334155",
    borderColor: "#64748B",
  },

  SERVICE_COMPLETED: {
    color: "#ECFEF6",
    textColor: "#065F46",
    borderColor: "#10B981",
  },

  /**
   * ============================================================
   * APPROVAL / COMMON WORKFLOW STATUSES
   * ============================================================
   */

  PENDING: {
    color: "#FEFCE8",
    textColor: "#854D0E",
    borderColor: "#EAB308",
  },

  CANCELLED: {
    color: "#FFE4E6",
    textColor: "#9F1239",
    borderColor: "#F43F5E",
  },

  IN_PROGRESS: {
    color: "#EEF2FF",
    textColor: "#3730A3",
    borderColor: "#6366F1",
  },

  /**
   * ============================================================
   * PACKING STATUSES
   * ============================================================
   */

  PARTIALLY_PACKED: {
    color: "#EDE9FE",
    textColor: "#4338CA",
    borderColor: "#7C3AED",
  },

  PACKED: {
    color: "#DCFCE7",
    textColor: "#14532D",
    borderColor: "#16A34A",
  },

  /**
   * ============================================================
   * DISPATCH & DELIVERY STATUSES
   * ============================================================
   */

  PENDING_DISPATCH: {
    color: "#FFF1F2",
    textColor: "#9F1239",
    borderColor: "#FB7185",
  },

  DISPATCHED: {
    color: "#F5F3FF",
    textColor: "#5B21B6",
    borderColor: "#A78BFA",
  },

  DELIVERED: {
    color: "#ECFDF5",
    textColor: "#047857",
    borderColor: "#059669",
  },

  /**
   * ============================================================
   * PAYMENT MANAGEMENT STATUSES
   * ============================================================
   */

  PARTIALLY_PAID: {
    color: "#FFEDD5",
    textColor: "#9A3412",
    borderColor: "#EA580C",
  },

  FULLY_PAID: {
    color: "#CCFBF1",
    textColor: "#134E4A",
    borderColor: "#0D9488",
  },

  PAID: {
    color: "#ECFEFF",
    textColor: "#0F766E",
    borderColor: "#0891B2",
  },

  UNPAID: {
    color: "#FEF2F2",
    textColor: "#991B1B",
    borderColor: "#DC2626",
  },

  /**
   * ============================================================
   * PBG / RECEIPT STATUSES
   * ============================================================
   */

  RECEIVED: {
    color: "#EFF6FF", // soft blue background
    textColor: "#1E40AF", // deep blue text
    borderColor: "#60A5FA",
  },

  RECIEVED: {
    color: "#FAF5FF", // soft violet background
    textColor: "#6B21A8", // strong purple text
    borderColor: "#C084FC",
  },

  NOT_RECEIVED: {
    color: "#FEF3C7",
    textColor: "#92400E",
    borderColor: "#F59E0B",
  },

  NOT_RECIEVED: {
    color: "#FFFBEB",
    textColor: "#78350F",
    borderColor: "#EAB308",
  },

  WAIVED: {
    color: "#F8FAFC",
    textColor: "#475569",
    borderColor: "#CBD5E1",
  },
};

interface StatusChipProps {
  status?: string | null;
  labelOverride?: string;
}

const StatusChip: React.FC<StatusChipProps> = ({ status, labelOverride }) => {
  if (!status) return "-";

  const normalizedKey = normalizeStatusKey(status);
  const config = STATUS_CONFIG[normalizedKey];
  console.log("STATUS:", status, "→", normalizedKey);

  const label =
    labelOverride ?? config?.label ?? toReadableLabel(normalizedKey);

  return (
    // <Tooltip title={label}>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.5,
        py: 0.5,
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        backgroundColor: config?.color ?? "#E5E7EB",
        color: config?.textColor ?? "#374151",
        border: `1px solid ${config?.borderColor ?? "rgba(0,0,0,0.05)"}`,
      }}
    >
      {label}
    </Box>
    // </Tooltip>
  );
};

export default StatusChip;
